import { Outlet, Navigate, useLocation } from "react-router-dom";
import { LeagueProvider, useLeague } from "../context/LeagueContext";
import { DraftProvider, useDraftContext } from "../context/DraftContext";
import LeagueSkeleton from "./league/LeagueSkeleton";
import LeagueNavBar from "./nav/LeagueNavBar";
import TabBar from "./nav/TabBar";
import DemoBar from "./nav/DemoBar";
import { useAuth } from "../context/AuthContext";

// Which league tabs have been migrated to the colour tokens.
//
// The nav bar and the tab bar are shared by every tab, so theming them at the
// layout would put dark chrome around a page still painting hardcoded light
// hexes, which looks more broken than no dark mode at all. The allowlist is
// the honest version of "we are doing this one page at a time": add a tab here
// when its own colours are tokenised, and delete the list once they all are.
const THEMED_TABS = new Set(["league", "lineup", "scoreboard", "teams", "draft-room"]);

function LeagueLayoutInner() {
  const { loading: leagueLoading, unavailable, isDemo } = useLeague();
  const { currentUser } = useAuth();
  const { pathname } = useLocation();
  const themed = THEMED_TABS.has(pathname.split("/").filter(Boolean).pop());
  const { loading: draftLoading } = useDraftContext();
  const loading = leagueLoading || draftLoading;

  // The league was deleted, or belongs to someone else and RLS hides it. Either
  // way the child pages have nothing to render, so send the user home instead of
  // letting them fail one query at a time. Checked before the loading branch:
  // an unavailable league would otherwise show a skeleton on the way out.
  //
  // Signed out, "unavailable" means any league but the public demo (league
  // routes are not behind PrivateRoute, so RLS decides). Send them to log in
  // and back here afterwards, as PrivateRoute used to.
  if (unavailable && !currentUser) {
    sessionStorage.setItem("authRedirect", pathname);
    return <Navigate to="/login" replace />;
  }
  if (unavailable) return <Navigate to="/home" replace />;

  // The document scrolls naturally rather than a fixed-height inner
  // container: on mobile Safari (and in-app browsers like ngrok's), 100vh
  // doesn't track the true visible viewport as the browser chrome
  // collapses/expands, which clips a nested `height: 100vh` + `overflow-y:
  // auto` scroll region. Natural document scroll doesn't have that failure
  // mode. LeagueNavBar is `position: sticky` and TabBar is `position: fixed`,
  // so both still stay in place without a dedicated scroll container.
  return (
    <div
      className={themed ? "theme-auto" : undefined}
      style={themed ? { backgroundColor: "var(--color-bg-page)", minHeight: "100vh" } : undefined}
    >
      <LeagueNavBar />
      {/* The demo bar floats over the page bottom, so the page gets room to
          scroll its last row clear of it. */}
      <div style={{ paddingBottom: isDemo ? "calc(49px + env(safe-area-inset-bottom) + 84px)" : "calc(49px + env(safe-area-inset-bottom))" }}>
        {/* The chrome stays mounted while the contexts load, so opening a
            league no longer blanks the screen to a full-page splash. The nav
            bar falls back to "League" until the name arrives and TabBar
            renders nothing until it knows whether the draft is complete, so
            neither shows a wrong state in the meantime. */}
        {loading ? <LeagueSkeleton /> : <Outlet />}
      </div>
      {isDemo && !loading && <DemoBar />}
      <TabBar />
    </div>
  );
}

function LeagueLayout() {
  return (
    <LeagueProvider>
      <DraftProvider>
        <LeagueLayoutInner />
      </DraftProvider>
    </LeagueProvider>
  );
}

export default LeagueLayout;
